import React, { useEffect, useMemo, useState } from "react";
import "./admin-order.css";
import {
  deleteOrderForAdmin,
  getAllOrdersForAdmin,
  markAsFailedForAdmin,
  markAsSuccessForAdmin,
} from "../../api/order.api";
import ToastNotification, { showToast } from "../../components/toasts/ToastNotification";
import { FaSearch } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import { productTypeText, statusText } from "../../utils";
import ConfirmModal from "../../components/modal/confirm-modal";
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { FaEye } from "react-icons/fa6";
import { FaBan, FaCheckCircle } from "react-icons/fa";
import { useUser } from "../../context/UserContext";
import { hasPermission } from "../../utils/auth";
import Pagination from "../../components/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";

const ITEMS_PER_PAGE = 8;

export const AdminOrder = () => {
  const [isFetching, setIsFetching] = useState(false);
  const [selected, setSelected] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebounce(searchInput, 500);

  const [currentPage, setCurrentPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const { user } = useUser();

  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState({ orderId: "", type: "" });

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Admin - Đơn hàng";
    fetchOrders();
  }, [currentPage, selected, debouncedSearchInput]);

  useEffect(() => {
      setCurrentPage(1);
    }, [debouncedSearchInput]);

  const summary = useMemo(() => {
    return orders.reduce(
      (acc, item) => {
        if (item.order_status === "success") acc.success += 1;
        if (item.order_status === "processing") acc.processing += 1;
        if (item.order_status === "failed") acc.failed += 1;
        return acc;
      },
      { success: 0, processing: 0, failed: 0 }
    );
  }, [orders]);
  const isActionBusy = Boolean(actionLoading.orderId);

  const fetchOrders = async (showLoader = true) => {
    try {
      if (showLoader) setIsFetching(true);
      const res = await getAllOrdersForAdmin(
        ITEMS_PER_PAGE,
        currentPage,
        debouncedSearchInput.trim(),
        selected
      );

      if (res.success) {
        setOrders(res.data.orders || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalOrders(res.data.total || 0);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      if (showLoader) setIsFetching(false);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  };

  const handleDeleteOrder = async () => {
    if (isActionBusy) return;
    try {
      setActionLoading({ orderId: selectedIdToDelete, type: "delete" });
      setIsModalOpen(false);
      const res = await deleteOrderForAdmin(selectedIdToDelete);

      if (res.success) {
        showToast("Xóa đơn hàng thành công", "success");

        await fetchOrders(false);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setActionLoading({ orderId: "", type: "" });
    }
  };

  const handleMarkAsSuccessOrder = async (orderId) => {
    if (isActionBusy) return;
    try {
      setActionLoading({ orderId, type: "success" });
      const res = await markAsSuccessForAdmin(orderId, user?._id);

      if (res?.success) {
        showToast("Bạn đã hoàn thành đơn", "success");
        await fetchOrders(false);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setActionLoading({ orderId: "", type: "" });
    }
  };

  const handleMarkAsFailedOrder = async (orderId) => {
    if (isActionBusy) return;
    try {
      setActionLoading({ orderId, type: "failed" });
      const res = await markAsFailedForAdmin(orderId, user?._id);

      if (res?.success) {
        showToast("Bạn đã hủy đơn", "warn");
        await fetchOrders(false);
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setActionLoading({ orderId: "", type: "" });
    }
  };

  return (
    <div className="admin-order-page">
      <ToastNotification />
      <div className="admin-order-shell">
        <div className="admin-order-header">
          <div>
            <p className="admin-order-breadcrumb">Quản trị / Đơn hàng</p>
            <h1 className="admin-order-title">Danh sách đơn hàng</h1>
          </div>
          <div className="admin-order-kpi">
            <div className="admin-order-kpi-item">
              <span>Tổng đơn</span>
              <strong>{totalOrders}</strong>
            </div>
            <div className="admin-order-kpi-item">
              <span>Trang hiện tại</span>
              <strong>{orders.length}</strong>
            </div>
            <div className="admin-order-kpi-item">
              <span>Đang xử lý</span>
              <strong>{summary.processing}</strong>
            </div>
          </div>
        </div>

        <div className="admin-order-toolbar">
          <div className="admin-order-search-wrap">
            <FaSearch className="admin-order-search-icon" />
            <input
              type="text"
              placeholder="Tìm kiếm theo mã đơn hàng"
              className="admin-order-search"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <select
            className="admin-order-filter"
            value={selected}
            onChange={(e) => {
              setSelected(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="all">Tất cả</option>
            <option value="success">Thành công</option>
            <option value="processing">Đang xử lý</option>
            <option value="failed">Thất bại</option>
          </select>
        </div>

        {isFetching && orders.length === 0 ? (
          <div className="loader-container">
            <HashLoader color="var(--hash-loader-color)" />
          </div>
        ) : (
          <>
            <div className="admin-order-table-wrap">
              <table className="admin-order-table">
              <thead>
                <tr>
                  <th>STT</th>
                  <th>Mã giao dịch</th>
                  <th>Tên người dùng</th>
                  <th>Số tiền</th>
                  <th>Loại</th>
                  <th>Trạng thái</th>
                  <th>Thời gian</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {orders?.length > 0 ? (
                  orders.map((tx, index) => (
                    <tr key={tx.orderId}>
                      <td className="admin-order-cell-muted">
                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                      </td>
                      <td className="admin-order-cell-code">{tx.orderId}</td>
                      <td>
                        <a className="admin-order-user-link" href={`/super-admin/users/view/${tx.userId?._id}`}>
                          {tx.userId?.username}
                        </a>
                      </td>
                      <td className="admin-order-cell-price">{tx.order_final_amount.toLocaleString()}đ</td>
                      <td>{productTypeText(tx.order_type)}</td>
                      <td>
                        <span className={`admin-order-status admin-order-status-${tx.order_status}`}>
                          {statusText(tx.order_status)}
                        </span>
                      </td>
                      <td className="admin-order-cell-muted">{new Date(tx.createdAt).toLocaleString("vi-VN")}</td>
                      <td>
                        <div className="admin-order-actions">
                          <button
                            className="admin-order-action admin-order-action-view"
                            onClick={() => navigate(`/super-admin/orders/view/${tx.orderId}`)}
                            title="Xem chi tiết"
                            disabled={isActionBusy}
                          >
                            <FaEye />
                          </button>
                          {hasPermission(user?.role, "cancel:order") && (
                            <button
                              disabled={
                                tx.order_status === "success" ||
                                tx.order_status === "failed" ||
                                isActionBusy
                              }
                              onClick={() => handleMarkAsFailedOrder(tx.orderId)}
                              className="admin-order-action admin-order-action-failed"
                              title="Từ chối đơn hàng"
                            >
                              <FaBan />
                            </button>
                          )}
                          {hasPermission(user?.role, "confirm:order") && (
                            <button
                              disabled={
                                tx.order_status === "success" ||
                                tx.order_status === "failed" ||
                                isActionBusy
                              }
                              onClick={() => handleMarkAsSuccessOrder(tx.orderId)}
                              className="admin-order-action admin-order-action-success"
                              title="Duyệt đơn hàng"
                            >
                              <FaCheckCircle />
                            </button>
                          )}
                          {hasPermission(user?.role, "delete:order") && (
                            <button
                              onClick={() => {
                                setIsModalOpen(true);
                                setSelectedIdToDelete(tx.orderId);
                              }}
                              className="admin-order-action admin-order-action-delete"
                              disabled={isActionBusy}
                              title="Xóa đơn hàng"
                            >
                              <MdDelete />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8}>
                      <p className="admin-order-empty">Không có đơn hàng phù hợp bộ lọc.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={handlePrevPage}
                onNext={handleNextPage}
              />
            )}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={handleDeleteOrder}
        onClose={() => setIsModalOpen(false)}
        message={"Xác nhận bạn đang xóa một đơn hàng"}
        title={"Xóa đơn hàng"}
      />
    </div>
  );
};
